<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12" md="8" class="text-justify">
        <h2 class="display-2 text-center text-secondary">Stardle (Alpha version)</h2>
        <p class="text-body-1">
          Each day, a secret Star Wars character is chosen. Can you guess who it is? Type the name and get hints for each attribute. Use your knowledge and deduction to win!
        </p>
      </v-col>
    </v-row>
    <!-- Legend Row -->
    <v-row justify="center" class="mb-4">
      <v-col cols="12" md="8">
        <v-card class="pa-2">
          <h4 class="text-h6 mb-2">Legend</h4>
          <div>
            <span class="text-success"><strong>Green:</strong> Correct value</span><br>
            <span class="text-error"><strong>Red:</strong> Incorrect value</span><br>
            <span class="text-warning"><strong>Yellow:</strong> Your guess is too high</span><br>
            <span class="text-info"><strong>Blue:</strong> Your guess is too low</span>
          </div>
        </v-card>
      </v-col>
    </v-row>
    <v-row justify="center">
      <v-col cols="12" md="8" class="text-center">
        <v-card class="pa-4 text-center">
          <!-- Mystery Character Panel -->
          <v-card class="mb-4 pa-4">
            <h3 class="text-h5 mb-2">Mystery Character</h3>
            <v-row>
              <v-col cols="6" sm="4"><strong>Name: </strong> {{ mysteryCharacter.name }}</v-col>
              <v-col cols="6" sm="4"><strong>Specy: </strong> {{ mysteryCharacter.specy }}</v-col>
              <v-col cols="6" sm="4"><strong>Gender: </strong> {{ mysteryCharacter.gender }}</v-col>
              <v-col cols="6" sm="4"><strong>Homeworld: </strong> {{ mysteryCharacter.homeworld }}</v-col>
              <v-col cols="6" sm="4"><strong>Height: </strong> {{ mysteryCharacter.heightDisplay }}</v-col>
              <v-col cols="6" sm="4"><strong>Mass: </strong> {{ mysteryCharacter.massDisplay }}</v-col>
              <v-col cols="12" sm="6"><strong>Affiliation: </strong>
                <span v-if="mysteryCharacter.affiliation.length">{{ mysteryCharacter.affiliation.join(', ') }}</span>
                <span v-else>?</span>
              </v-col>
              <v-col cols="12" sm="6"><strong>Occupation: </strong>
                <span v-if="mysteryCharacter.occupation.length">{{ mysteryCharacter.occupation.join(', ') }}</span>
                <span v-else>?</span>
              </v-col>
            </v-row>
          </v-card>

          <!-- Guess Input -->
          <v-autocomplete
            v-model:search="search"
            v-model="selectedCharacter"
            :items="filteredOptions"
            label="Guess the character"
            outlined
            hide-details
            :menu-props="{ openOnClick: false }"
            hide-no-data
            @keydown.enter="submitGuess"
            :disabled="win"
          ></v-autocomplete>
          <v-btn color="primary" @click="submitGuess" class="mt-2" :disabled="win">Submit</v-btn>
          <p v-if="feedbackMessage" class="mt-3 text-success">{{ feedbackMessage }}</p>
          <p v-if="win" class="mt-3 text-info">You won! Come back tomorrow for a new mystery character.</p>

          <!-- Last Guess Feedback -->
          <div v-if="guessResult && guessHistory.length" class="mt-3">
            <h3 class="text-h6">Last Guess Feedback:</h3>
            <v-list>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>
                    Height:
                    <span :class="getHintClass(guessResult.height)">
                      {{ guessHistory[guessHistory.length - 1].character.height }}
                    </span>
                  </v-list-item-title>
                  <v-list-item-title>
                    Mass:
                    <span :class="getHintClass(guessResult.mass)">
                      {{ guessHistory[guessHistory.length - 1].character.mass }}
                    </span>
                  </v-list-item-title>
                  <v-list-item-title>
                    Gender:
                    <span :class="getHintClass(guessResult.gender)">
                      {{ guessHistory[guessHistory.length - 1].character.gender }}
                    </span>
                  </v-list-item-title>
                  <v-list-item-title>
                    Homeworld:
                    <span :class="getHintClass(guessResult.homeworld)">
                      {{ guessHistory[guessHistory.length - 1].character.homeworld }}
                    </span>
                  </v-list-item-title>
                  <v-list-item-title>
                    Specy:
                    <span :class="getHintClass(guessResult.specy)">
                      {{ guessHistory[guessHistory.length - 1].character.specy }}
                    </span>
                  </v-list-item-title>
                  <v-list-item-title>
                    Affiliation:
                    <span v-for="(aff, idx) in guessHistory[guessHistory.length - 1].character.affiliation" :key="aff">
                      <span :class="getHintClass(guessHistory[guessHistory.length - 1].feedback.affiliation[idx])">
                        {{ aff }}
                      </span>
                      <span v-if="idx < guessHistory[guessHistory.length - 1].character.affiliation.length - 1">, </span>
                    </span>
                  </v-list-item-title>
                  <v-list-item-title>
                    Occupation:
                    <span v-for="(occ, idx) in guessHistory[guessHistory.length - 1].character.occupation" :key="occ">
                      <span :class="getHintClass(guessHistory[guessHistory.length - 1].feedback.occupation[idx])">
                        {{ occ }}
                      </span>
                      <span v-if="idx < guessHistory[guessHistory.length - 1].character.occupation.length - 1">, </span>
                    </span>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>

          <!-- Guess History -->
          <div v-if="guessHistory.length" class="mt-5">
            <h3 class="text-h6">Guess History</h3>
            <v-list>
              <v-list-item v-for="(guess, idx) in guessHistory" :key="idx">
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>{{ guess.character.name }}</strong>
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <div class="guess-attributes">
                      <div><strong>Specy:</strong> {{ Array.isArray(guess.character.specy) ? guess.character.specy.join(', ') : guess.character.specy }}</div>
                      <div><strong>Gender:</strong> {{ guess.character.gender }}</div>
                      <div><strong>Homeworld:</strong> {{ guess.character.homeworld }}</div>
                      <div><strong>Height:</strong> {{ guess.character.height }}</div>
                      <div><strong>Mass:</strong> {{ guess.character.mass }}</div>
                      <div><strong>Affiliation:</strong>
                        <span v-for="(aff, i) in guess.character.affiliation" :key="aff">
                          {{ aff }}<span v-if="i < guess.character.affiliation.length - 1">, </span>
                        </span>
                      </div>
                      <div><strong>Occupation:</strong>
                        <span v-for="(occ, i) in guess.character.occupation" :key="occ">
                          {{ occ }}<span v-if="i < guess.character.occupation.length - 1">, </span>
                        </span>
                      </div>
                    </div>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>

          <!-- Reveal Secret -->
          <div class="mt-5">
            <v-btn color="secondary" @click="revealSecret">Give Up</v-btn>
            <p v-if="revealed" class="mt-4">The secret character was: <strong>{{ secretCharacter }}</strong></p>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import apiClient from "@/apiClient";
import { useHead } from '@unhead/vue';

export default {
  data() {
    return {
      selectedCharacter: "",
      characterOptions: [],
      guessResult: null,
      feedbackMessage: "",
      revealed: false,
      secretCharacter: "",
      search: "",
      guessHistory: [],
      mysteryCharacter: {
        name: "?",
        specy: "?",
        gender: "?",
        homeworld: "?",
        heightDisplay: "?",
        massDisplay: "?",
        affiliation: [],
        occupation: []
      },
      minHeight: null,
      maxHeight: null,
      minMass: null,
      maxMass: null,
      win: false 
    };
  },
  setup() {
    useHead({
      title: "Stardle – Guess the Daily Star Wars Character & Expand the Galaxy",
      meta: [
        {
          name: "description",
          content: "Play Stardle, the daily Star Wars character puzzle! Test your intuition, uncover hidden knowledge, and help expand the Holonet Galactic Encyclopedia. Can you guess today’s secret character?",
        },
        {
          property: "og:title",
          content: "Stardle – Guess the Daily Star Wars Character & Expand the Galaxy",
        },
        {
          property: "og:description",
          content: "A Bothan researcher needs your help! Solve the daily Stardle puzzle to uncover the secret Star Wars character hidden in the Galactic Library. Play now and contribute to the galaxy’s knowledge!",
        },
        {
          property: "og:url",
          content: "https://sw-holonet.com/games/stardle",
        },
        { property: "og:type", content: "website" },
        {
          name: "twitter:title",
          content: "Stardle – Guess the Daily Star Wars Character",
        },
        {
          name: "twitter:description",
          content: "Can you guess today’s secret Star Wars character? Play Stardle now and help expand the Galactic Encyclopedia!",
        },
        { name: "twitter:card", content: "summary" },
        {
          name: "keywords",
          content: "Stardle, Star Wars puzzle, daily Star Wars game, Star Wars character, Bothan researcher, Galactic Library, Star Wars trivia, Holonet game, Star Wars knowledge",
        },
      ],
    });
  },
  computed: {
    filteredOptions() {
      // Don't show suggestions until at least one character is typed
      if (!this.search || this.search.length < 1) return [];
      // Exclude already guessed characters
      const guessedNames = this.guessHistory.map(g => g.character.name);
      return this.characterOptions.filter(
        name =>
          name.toLowerCase().includes(this.search.toLowerCase()) &&
          !guessedNames.includes(name)
      );
    }
  },
  mounted() {
    this.fetchCharacters();
  },
  methods: {
    formatFeedback(val) {
      if (Array.isArray(val)) return val.length ? val.join(', ') : 'No match';
      if (typeof val === 'boolean') return val ? 'Correct' : 'Incorrect';
      if (typeof val === 'string') return val;
      return val;
    },
    async fetchCharacters() {
      try {
        const response = await apiClient.get("/api/stardle/characters");
        this.characterOptions = response.data.map(c => c.name);
      } catch (error) {
        this.feedbackMessage = "Failed to load characters.";
      }
    },
    async submitGuess() {
      if (this.win) return; // Prevent further guesses after win
      if (!this.selectedCharacter) {
        this.feedbackMessage = "Please select a character.";
        return;
      }
      try {
        const response = await apiClient.post("/api/stardle/guess", { name: this.selectedCharacter });
        const guessCharacter = response.data.guess || {};
        const feedback = response.data.feedback || {};
        this.guessResult = feedback;
        this.win = response.data.result === true;        
        this.feedbackMessage = this.win ? "You found the secret character!" : "Keep trying!";
        this.guessHistory.push({ character: guessCharacter, feedback });
        this.updateMysteryCharacter(guessCharacter, feedback);
        if (this.win && response.data.secret) {
          this.revealed = true;
          this.secretCharacter = response.data.secret.name;
          this.mysteryCharacter = {
            ...response.data.secret,
            heightDisplay: response.data.secret.height,
            massDisplay: response.data.secret.mass,
            affiliation: response.data.secret.affiliation,
            occupation: response.data.secret.occupation
          };
        }
        this.selectedCharacter = "";
        this.search = "";
      } catch (error) {
        this.feedbackMessage = error.response?.data?.error || "An error occurred.";
        this.guessResult = null;
      }
    },
    updateMysteryCharacter(guess, feedback) {
      // Name: only reveal if correct
      if (feedback.name === true) this.mysteryCharacter.name = guess.name;
      // Specy, gender, homeworld: reveal if correct
      if (feedback.specy === true) this.mysteryCharacter.specy = guess.specy;
      if (feedback.gender === true) this.mysteryCharacter.gender = guess.gender;
      if (feedback.homeworld === true) this.mysteryCharacter.homeworld = guess.homeworld;
      // Affiliation/Occupation: accumulate correct values
      if (Array.isArray(feedback.affiliation) && Array.isArray(guess.affiliation)) {
        guess.affiliation.forEach((aff, idx) => {
          if (feedback.affiliation[idx] && !this.mysteryCharacter.affiliation.includes(aff)) {
            this.mysteryCharacter.affiliation.push(aff);
          }
        });
      }
      if (Array.isArray(feedback.occupation) && Array.isArray(guess.occupation)) {
        guess.occupation.forEach((occ, idx) => {
          if (feedback.occupation[idx] && !this.mysteryCharacter.occupation.includes(occ)) {
            this.mysteryCharacter.occupation.push(occ);
          }
        });
      }
      // Height/Mass: track min/max bounds
      if (typeof feedback.height === "string") 
      {
        if (feedback.height === "equal") {
          this.mysteryCharacter.heightDisplay = guess.height;
        } 
        else
        {
          if (feedback.height === "higher") 
          {
            this.minHeight = this.minHeight === null ? guess.height : Math.max(this.minHeight, guess.height);
          } 
          else if (feedback.height === "lower") 
          {
            this.maxHeight = this.maxHeight === null ? guess.height : Math.min(this.maxHeight, guess.height);
          }
          this.mysteryCharacter.heightDisplay = this.getHeightRange();
        }
      }
      if (typeof feedback.mass === "string") 
      {
        if (feedback.mass === "equal") {
          this.mysteryCharacter.massDisplay = guess.mass;
        }
        else
        {
          if (feedback.mass === "higher") 
          {
            this.minMass = this.minMass === null ? guess.mass : Math.max(this.minMass, guess.mass);
          } 
          else if(feedback.mass === "lower") 
          {
            this.maxMass = this.maxMass === null ? guess.mass : Math.min(this.maxMass, guess.mass);
          }
          this.mysteryCharacter.massDisplay = this.getMassRange();
        }
      }
    },
    getHeightRange() {
      if (this.minHeight !== null && this.maxHeight !== null) return `>${this.minHeight} and <${this.maxHeight}`;
      if (this.minHeight !== null) return `>${this.minHeight}`;
      if (this.maxHeight !== null) return `<${this.maxHeight}`;
      return "?";
    },
    getMassRange() {
      if (this.minMass !== null && this.maxMass !== null) return `>${this.minMass} and <${this.maxMass}`;
      if (this.minMass !== null) return `>${this.minMass}`;
      if (this.maxMass !== null) return `<${this.maxMass}`;
      return "?";
    },
    async revealSecret() {
      this.revealed = true;
      try {
        const response = await apiClient.get("/api/stardle/secret");
        this.secretCharacter = response.data.secret.name;
        this.mysteryCharacter = {
          ...response.data.secret,
          heightDisplay: response.data.secret.height,
          massDisplay: response.data.secret.mass,
          affiliation: response.data.secret.affiliation,
          occupation: response.data.secret.occupation
        };
        this.win=true;
      } catch (error) {
        this.secretCharacter = "Error fetching secret character.";
      }
    },
    getHintClass(hint) {
      if (hint === "Correct" || hint === true || hint === "equal") return "text-success";
      if (hint === "Incorrect" || hint === false) return "text-error";
      if (hint === "lower" || hint === "Too tall" || hint === "Too heavy") return "text-warning";
      if (hint === "higher" || hint === "Too short" || hint === "Too light") return "text-info";
      return "";
    }
  }
};
</script>

<style scoped>
.text-success { color: #28a745; }
.text-error { color: #dc3545; }
.text-warning { color: #ffc107; }
.text-info { color: #17a2b8; }
.guess-attributes > div {
  margin-bottom: 2px;
  word-break: break-word;
  white-space: pre-line;
}

/* Fix for v-list-item-title and v-list-item-subtitle truncation */
.v-list-item-title,
.v-list-item-subtitle {
  display: block !important;
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
}
</style>
